import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Clock3, ShieldAlert, XCircle } from "lucide-react";

import { apiFetch } from "@/lib/api-client";
import { Skeleton, SkeletonCard } from "@/components/skeleton";

type AccessRequestStatus = "pending" | "approved" | "denied";

interface AccessRequest {
  id: string;
  organizationId: string | null;
  requestedByUserId: string;
  requesterName: string;
  requesterEmail: string;
  requestedRole: string;
  resource: string;
  resourcePath: string | null;
  message: string | null;
  status: AccessRequestStatus;
  reviewedByUserId: string | null;
  reviewNote: string | null;
  reviewedAt: string | null;
  notificationSentAt: string | null;
  createdAt: string;
  updatedAt: string;
}

const STATUS_STYLES: Record<AccessRequestStatus, string> = {
  pending: "border-amber-500/25 bg-amber-500/10 text-amber-200",
  approved: "border-emerald-500/25 bg-emerald-500/10 text-emerald-200",
  denied: "border-rose-500/25 bg-rose-500/10 text-rose-200",
};

export default function AdminAccessRequestsPage() {
  const [requests, setRequests] = useState<AccessRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);

  const loadRequests = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await apiFetch<{ data: { requests: AccessRequest[] } }>("/v1/platform/access-requests");
      setRequests(result.data.requests ?? []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load access requests.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadRequests();
  }, [loadRequests]);

  const resolveRequest = async (requestId: string, action: "approve" | "deny") => {
    setActiveId(requestId);
    try {
      const result = await apiFetch<{ data: AccessRequest }>(`/v1/platform/access-requests/${requestId}/${action}`, {
        method: "POST",
        body: JSON.stringify({
          note: action === "approve"
            ? "Approved from platform access queue."
            : "Denied from platform access queue.",
        }),
      });
      setRequests((current) => current.map((request) => request.id === requestId ? result.data : request));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${action} access request.`);
    } finally {
      setActiveId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-56" />
        <SkeletonCard className="h-40" />
        <SkeletonCard className="h-40" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3">
        <ShieldAlert className="mt-1 h-5 w-5 text-[var(--color-primary)]" />
        <div>
          <h1 className="text-2xl font-semibold text-white">Access Requests</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Review permission escalations, grant the requested role, and notify the requester from one mounted control-plane surface.
          </p>
        </div>
      </div>

      {error ? (
        <div className="rounded-xl border border-rose-500/25 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
          {error}
        </div>
      ) : null}

      {requests.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[var(--color-border)] bg-[var(--color-background)] p-10 text-center text-sm text-muted-foreground">
          No access requests are waiting for review.
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map((request) => (
            <div key={request.id} className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded-full border px-2.5 py-1 text-xs font-medium ${STATUS_STYLES[request.status]}`}>
                      {request.status}
                    </span>
                    <span className="text-sm font-semibold text-white">{request.resource}</span>
                    <span className="text-xs text-muted-foreground">→ {request.requestedRole}</span>
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {request.requesterName} • {request.requesterEmail}
                    {request.organizationId ? ` • org ${request.organizationId}` : ""}
                  </p>
                  {request.message ? (
                    <p className="mt-3 text-sm text-slate-200">{request.message}</p>
                  ) : null}
                  <p className="mt-3 text-xs text-muted-foreground">
                    Requested {new Date(request.createdAt).toLocaleString()}
                    {request.reviewedAt ? ` • Reviewed ${new Date(request.reviewedAt).toLocaleString()}` : ""}
                    {request.notificationSentAt ? " • Notification sent" : ""}
                  </p>
                </div>
                {request.status === "pending" ? (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={activeId === request.id}
                      onClick={() => void resolveRequest(request.id, "approve")}
                      className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                    >
                      <CheckCircle2 className="h-4 w-4" />
                      Approve
                    </button>
                    <button
                      type="button"
                      disabled={activeId === request.id}
                      onClick={() => void resolveRequest(request.id, "deny")}
                      className="inline-flex items-center gap-2 rounded-xl border border-rose-500/30 px-4 py-2 text-sm font-semibold text-rose-200 disabled:opacity-60"
                    >
                      <XCircle className="h-4 w-4" />
                      Deny
                    </button>
                  </div>
                ) : (
                  <div className="inline-flex items-center gap-2 rounded-xl border border-[var(--color-border)] px-3 py-2 text-sm text-muted-foreground">
                    {request.status === "approved" ? <CheckCircle2 className="h-4 w-4" /> : request.status === "denied" ? <XCircle className="h-4 w-4" /> : <Clock3 className="h-4 w-4" />}
                    Resolved
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
